import { useEffect, useState } from "react";
import PointKeypad from "../PointKeypad";
import {
    customerPointRequest,
    getEntryPointList,
} from "../../api/point/point.api";

// 완료 아이콘
const CheckIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-24 w-24 text-white drop-shadow-md" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
    </svg>
);

// (관리자 아이콘은 좌상단 '관리자' 버튼 제거와 함께 삭제했다 — 2026-08-27)

const CustomerPointPage = () => {
    const [selectedEntryPoint, setSelectedEntryPoint] = useState();
    const [entryPoints, setEntryPoints] = useState([]);
    const [phoneNumber, setPhoneNumber] = useState("010");
    const [isComplete, setIsComplete] = useState(false); // 완료 상태 관리
    
    // 전화번호 포맷팅 함수
    const formatPhone = (raw) => {
        const digits = raw.replace(/\D/g, "").slice(0, 11);
        const len = digits.length;

        if (len < 4) return digits;
        if (len < 8) return `${digits.slice(0, 3)}-${digits.slice(3)}`;

        const isTen = len === 10;
        const midEnd = isTen ? 6 : 7;

        return `${digits.slice(0, 3)}-${digits.slice(3, midEnd)}-${digits.slice(midEnd)}`;
    };

    // 초기 진입점 목록 로드
    useEffect(() => {
        getEntryPointList().then(({ data }) => {
            if (data.length === 1) {
                setSelectedEntryPoint({ name: data[0].name, id: data[0].id });
            }
            setEntryPoints(data);
        });
    }, []);

    // 적립 요청 핸들러
    const handleSave = () => {
        if (phoneNumber.length < 10) return;

        customerPointRequest(selectedEntryPoint.id, formatPhone(phoneNumber)).then(() => {
            setIsComplete(true);
            setTimeout(() => {
                setPhoneNumber("010");
                setIsComplete(false);
            }, 2000);
        })
            .catch((err) => {
                console.error(err);
                alert("오류가 발생했습니다. 직원에게 문의해주세요.");
            });

    };

    // ---------------------------------------------------------
    // 1. 진입점 선택 화면
    // ---------------------------------------------------------
    if (!selectedEntryPoint) {
        return (
            /* 중앙정렬을 `justify-center` 가 아니라 카드 `m-auto` 로 한다 (2026-08-26).
               `min-h-screen` 이라 페이지가 늘어나서 `/point/view` 처럼 닿지 못하는 상태는 아니지만,
               중앙정렬이 걸린 채로 컨테이너에 높이가 생기면(레이아웃 변경 한 번이면 충분하다)
               넘친 위쪽이 잘리고 스크롤로도 복구되지 않는다. auto margin 은 그 상태가 원천적으로 없다.
               상세 = `pages/JoinPage.js` 주석 / 같은 결함의 실제 사례 = `PointViewPage.js` 진입점 모달. */
            <div className="min-h-screen min-h-[100dvh] bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex flex-col p-3 sm:p-4">
                <div className="bg-white rounded-3xl shadow-2xl w-full max-w-4xl m-auto p-[clamp(1rem,3.5dvh,2.5rem)]">
                    <h2 className="font-bold text-center text-gray-800 text-[clamp(1.125rem,3.4dvh,1.875rem)] mb-[clamp(0.75rem,3dvh,2.5rem)]">
                        태블릿을 사용할 매장을 선택해주세요
                    </h2>
                    {/* 카드 크기도 `dvh` 로 묶는다 — 세로가 짧은 태블릿에서 한 화면에 더 많이 들어온다 */}
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-[clamp(0.5rem,2dvh,1.5rem)]">
                        {entryPoints.map((entryPoint) => (
                            <button
                                key={entryPoint.id}
                                className="flex flex-col items-center justify-center bg-white border-2 border-gray-100 rounded-2xl hover:border-indigo-500 hover:bg-indigo-50 transition-all duration-200 gap-[clamp(0.375rem,1.5dvh,1rem)] p-[clamp(0.75rem,3dvh,2rem)]"
                                onClick={() => setSelectedEntryPoint({ name: entryPoint.name, id: entryPoint.id })}
                            >
                                <div className="rounded-full bg-gray-200 overflow-hidden shrink-0 w-[clamp(2.5rem,8dvh,5rem)] h-[clamp(2.5rem,8dvh,5rem)]">
                                    <img className="object-cover w-full h-full" alt={entryPoint.name} src={entryPoint.imageUrl} />
                                </div>
                                <span className="font-bold text-gray-700 text-center break-keep text-[clamp(0.875rem,2.2dvh,1.25rem)]">{entryPoint.name}</span>
                            </button>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    // ---------------------------------------------------------
    // 2. 완료 화면
    // ---------------------------------------------------------
    if (isComplete) {
        return (
            <div className="min-h-screen min-h-[100dvh] w-full bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 flex flex-col items-center justify-center animate-fade-in p-4">
                <div className="bg-white/20 backdrop-blur-md rounded-full shadow-inner border border-white/30 p-[clamp(1rem,4dvh,2.5rem)] mb-[clamp(0.75rem,3dvh,2rem)]">
                    <CheckIcon />
                </div>
                <h1 className="font-black text-white tracking-wide drop-shadow-md text-center text-[clamp(1.5rem,6dvh,3rem)] mb-[clamp(0.375rem,1.5dvh,1rem)]">적립 요청 완료!</h1>
                <p className="text-indigo-100 font-medium text-center text-[clamp(0.875rem,2.4dvh,1.25rem)]">잠시 후 초기 화면으로 돌아갑니다...</p>
            </div>
        );
    }

    // ---------------------------------------------------------
    // 3. 메인 번호 입력 화면
    // ---------------------------------------------------------
    return (
        /*
          높이 배분 원칙 (2026-08-26 개편) — 갤럭시탭처럼 **가로로 눕히면 세로가 짧은** 16:9·19:9
          태블릿에서 하단 '적립하기' 가 가려지고 키패드가 찌그러지던 문제를 구조로 없앤다.

          종전 = 상단 타이틀(`pt-8 pb-4`)·번호창(`h-20`)·버튼(`h-16`)이 **고정 높이**였고
          키패드만 `flex-1` 이었다. 게다가 그 `flex-1` 에 `min-h-0` 이 없어서
          **내용보다 작게 줄지 못하고**, 줄지 못한 만큼이 아래 형제(적립 버튼)를 화면 밖으로 밀어냈다.
          ⇒ 세로가 짧아지면 "키패드는 안 줄고 버튼이 사라지는" 정확히 반대 동작이 났다.

          지금 = 우선순위를 코드로 못박는다.
          ① **적립 버튼** — `flex-none` + 높이 `clamp` 하한 2.75rem. 절대 안 사라진다.
          ② **키패드** — `flex-1 min-h-0`. 남는 높이를 전부 가져간다(최우선).
          ③ **번호창·타이틀** — `flex-none` 이지만 높이·글자를 전부 `dvh` `clamp` 로 묶어
             화면이 짧아지면 **먼저 얇아진다.** 가독성 하한(번호 1.375rem)은 지킨다.
          `100dvh` = 주소창이 접히면 함께 줄어드는 높이(`100vh` 는 안 줄어 하단이 잘린다).
        */
        /* `touch-manipulation` = double-tap 확대를 꺼서 탭 지연(~300ms)을 없앤다.
           상세 = `components/PointKeypad.js` 의 '터치 입력감' 주석 */
        <div className="h-screen h-[100dvh] w-full bg-gradient-to-br from-indigo-50 via-white to-blue-50 flex flex-col overflow-hidden relative touch-manipulation">

            {/*
              🔴 좌상단 '관리자' 버튼을 **제거했다** (2026-08-27 사용자 지시).
                 이 화면은 매장에 두고 **고객이 직접 만지는** 태블릿이다. 그 버튼은
                 `/point/users`(포인트 고객 조회 = 다른 고객들의 전화번호·포인트가 보이는 관리자 화면)로
                 바로 넘어가므로 고객이 눌러버릴 위험이 있었다. `[SECURITY_RISK]`
                 관리자 화면은 매장용(`/point/view`) 사이드바 하단 '관리자 페이지 이동' 으로만 간다.

              대신 좌하단에 **매장 재선택** 버튼만 남긴다 — 설치·이동 시 필요하고,
              고객이 눌러도 다시 매장을 고르는 화면일 뿐이라 노출되는 정보가 없다.
              흐릿한 작은 원문자 ✕ 로 두어 고객 눈에 잘 띄지 않게 한다.
            */}
            <button
                type="button"
                aria-label="매장 다시 선택"
                title="매장 다시 선택"
                className="absolute bottom-2 left-2 z-50 flex items-center justify-center w-7 h-7 rounded-full border border-slate-200 bg-white/40 text-slate-300 text-[13px] leading-none hover:bg-white hover:text-slate-500 transition-colors touch-manipulation [-webkit-tap-highlight-color:transparent]"
                onClick={() => setSelectedEntryPoint(undefined)}
            >
                ✕
            </button>

            {/* [상단] 매장명 및 안내 문구 — 세로가 짧아지면 가장 먼저 얇아진다 */}
            <div className="flex-none px-4 pt-[clamp(0.5rem,2dvh,2rem)] pb-[clamp(0.25rem,1dvh,1rem)] text-center">
                <h2 className="text-indigo-400 font-bold tracking-widest text-[clamp(0.5rem,1.4dvh,0.875rem)]">MEMBERSHIP POINT</h2>
                <h1 className="font-black text-slate-800 leading-tight truncate text-[clamp(0.95rem,2.6dvh,1.5rem)]">{selectedEntryPoint.name}</h1>
            </div>

            {/* [중단] 번호 표시 + 키패드 + 버튼. `min-h-0` 없으면 아래 버튼이 밀려 나간다 */}
            <div className="flex-1 min-h-0 flex flex-col px-4 pb-[clamp(0.5rem,2dvh,1.5rem)] w-full max-w-lg mx-auto">

                {/* 전화번호 표시창 — `whitespace-nowrap` 으로 두 줄로 넘치지 않게 못박는다 */}
                <div className="flex-none flex items-center justify-center bg-white rounded-2xl shadow-sm border border-indigo-100 h-[clamp(2.75rem,8dvh,5rem)] mb-[clamp(0.375rem,1.5dvh,1rem)]">
                    <span className={`font-black whitespace-nowrap tracking-[0.06em] transition-colors text-[clamp(1.375rem,5dvh,2.25rem)] ${phoneNumber ? 'text-indigo-900' : 'text-indigo-200'}`}>
                        {phoneNumber ? formatPhone(phoneNumber) : "010-0000-0000"}
                    </span>
                </div>

                {/* [최우선] 키패드 — 남는 높이 전부. C(전체 지우기)·←(한 자 지우기) 색 구분은 컴포넌트에 있다 */}
                <div className="flex-1 min-h-0">
                    <PointKeypad
                        leftKey="C"
                        onAppend={(d) => setPhoneNumber(phoneNumber + d)}
                        onClear={() => setPhoneNumber("010")}
                        onBackspace={() => setPhoneNumber(phoneNumber.substring(0, phoneNumber.length - 1))}
                    />
                </div>

                {/* [하단] 적립 버튼 — `flex-none` + 높이 하한. 절대 가려지지 않는다 */}
                <div className="flex-none mt-[clamp(0.375rem,1.5dvh,1rem)] h-[clamp(2.75rem,7.5dvh,4rem)]">
                    <button
                        onClick={handleSave}
                        disabled={phoneNumber.length < 10}
                        className={`w-full h-full rounded-2xl font-bold shadow-lg transition-all active:scale-95 flex items-center justify-center text-[clamp(1rem,2.8dvh,1.25rem)]
                            ${phoneNumber.length >= 10
                            ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-indigo-300 hover:brightness-110'
                            : 'bg-slate-200 text-slate-400 cursor-not-allowed'}`}
                    >
                        적립하기
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CustomerPointPage;